/**
 * Security Posture Page
 * 
 * Makes security posture discoverable and reviewable.
 * Makes M2.J2.1 (Security posture is discoverable and reviewable) true.
 */

import { useState } from 'react';
import {
  Shield,
  CheckCircle,
  AlertCircle,
  Clock,
  Lock,
  Key,
  Database,
  Server,
  Activity,
  FileCheck,
  Download,
  Mail,
  ExternalLink,
} from 'lucide-react';
import {
  getSecurityPosture,
  getControlsByCategory,
  generateSecuritySummary,
  type SecurityCategory,
  type SecurityControl,
  type SecurityPosture as SecurityPostureType,
} from '../lib/securityPosture';

const CATEGORY_CONFIG: Record<SecurityCategory, { label: string; icon: typeof Shield }> = {
  authentication: { label: 'Authentication', icon: Key },
  authorization: { label: 'Authorization', icon: Lock },
  data_protection: { label: 'Data Protection', icon: Database },
  infrastructure: { label: 'Infrastructure', icon: Server },
  compliance: { label: 'Compliance', icon: FileCheck },
  audit: { label: 'Audit', icon: Activity },
  availability: { label: 'Availability', icon: CheckCircle },
};

function StatusBadge({ status }: { status: SecurityControl['status'] }) {
  const config = {
    current: { color: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]', icon: CheckCircle },
    pending: { color: 'bg-[var(--mustard-dim)] text-[var(--mustard)]', icon: Clock },
    not_applicable: { color: 'bg-[var(--hover-tint)] text-[var(--text-sec)]', icon: AlertCircle },
  };
  
  const { color, icon: Icon } = config[status];
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
      <Icon className="h-3 w-3" />
      {status.replace('_', ' ')}
    </span>
  );
}

function ControlCard({ control }: { control: SecurityControl }) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="font-medium text-[var(--text-pri)]">{control.name}</h4>
          <p className="text-sm text-[var(--text-sec)] mt-1">{control.description}</p>
        </div>
        <StatusBadge status={control.status} />
      </div>
      
      {expanded && (
        <div className="mt-4 pt-4 border-t border-[var(--rule)]">
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-[var(--text-ter)]">Implementation</dt>
              <dd className="text-[var(--text-pri)] mt-1">{control.implementation}</dd>
            </div>
            <div>
              <dt className="text-[var(--text-ter)]">Evidence Type</dt>
              <dd className="text-[var(--text-pri)] mt-1 capitalize">{control.evidenceType}</dd>
            </div>
            {control.lastVerified && (
              <div>
                <dt className="text-[var(--text-ter)]">Last Verified</dt>
                <dd className="text-[var(--text-pri)] mt-1">{control.lastVerified}</dd>
              </div>
            )}
            {control.relatedCompliance && (
              <div>
                <dt className="text-[var(--text-ter)]">Related Compliance</dt>
                <dd className="text-[var(--text-pri)] mt-1">
                  {control.relatedCompliance.join(', ')}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}
      
      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-3 text-sm text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 font-medium"
      >
        {expanded ? 'Show less' : 'Show details'}
      </button>
    </div>
  );
}

export default function SecurityPosturePage() {
  const [posture] = useState<SecurityPostureType>(() => getSecurityPosture());
  const [selectedCategory, setSelectedCategory] = useState<SecurityCategory | 'all'>('all');
  
  const categories = Object.keys(CATEGORY_CONFIG) as SecurityCategory[];
  
  const filteredControls = selectedCategory === 'all'
    ? posture.controls
    : getControlsByCategory(selectedCategory);
  
  const currentCount = posture.controls.filter(c => c.status === 'current').length;
  const coveragePercent = Math.round((currentCount / posture.controls.length) * 100);
  
  const handleDownload = () => {
    const summary = generateSecuritySummary();
    const blob = new Blob([summary], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'peak-priorities-security-posture.md';
    a.click();
    URL.revokeObjectURL(url);
  };
  
  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[var(--green-dim)] flex items-center justify-center">
              <Shield className="h-6 w-6 text-[var(--green)]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[var(--text-pri)]">Security Posture</h1>
              <p className="text-[var(--text-sec)]">
                Version {posture.version} • Last updated {posture.lastUpdated}
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={handleDownload}
          className="inline-flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors"
        >
          <Download className="h-4 w-4" />
          Download Report
        </button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-3xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{coveragePercent}%</div>
          <div className="text-sm text-[var(--text-sec)] mt-1">Control Coverage</div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-3xl font-bold text-[var(--text-pri)]">{currentCount}</div>
          <div className="text-sm text-[var(--text-sec)] mt-1">Active Controls</div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-3xl font-bold text-consulting-slate-blue-600 capitalize">
            {posture.overallStatus.replace('_', ' ')}
          </div>
          <div className="text-sm text-[var(--text-sec)] mt-1">Overall Status</div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="text-3xl font-bold text-[var(--text-pri)]">{categories.length}</div>
          <div className="text-sm text-[var(--text-sec)] mt-1">Categories</div>
        </div>
      </div>
      
      {/* Compliance Status */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-8">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Compliance Status</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {Object.entries(posture.complianceStatus).map(([standard, status]) => (
            <div key={standard} className="flex items-center justify-between p-3 bg-[var(--surface)]">
              <span className="font-medium text-[var(--text-pri)]">{standard.replace('_', ' ')}</span>
              <StatusBadge status={status} />
            </div>
          ))}
        </div>
      </div>
      
      {/* Security Contacts */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-8">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Security Contacts</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {posture.contacts.map((contact, i) => (
            <div key={i} className="p-4 bg-[var(--surface)]">
              <div className="font-medium text-[var(--text-pri)]">{contact.role}</div>
              <div className="text-sm text-[var(--text-sec)] mt-1">{contact.responsibility}</div>
              <a
                href={`mailto:${contact.email}`}
                className="inline-flex items-center gap-1 mt-2 text-sm text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700"
              >
                <Mail className="h-4 w-4" />
                {contact.email}
              </a>
            </div>
          ))}
        </div>
      </div>
      
      {/* Controls */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">Security Controls</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-3 py-1.5  text-sm font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-consulting-slate-blue-100 text-consulting-slate-blue-700'
                  : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
              }`}
            >
              All
            </button>
            {categories.map(cat => {
              const config = CATEGORY_CONFIG[cat];
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5  text-sm font-medium transition-colors ${
                    selectedCategory === cat
                      ? 'bg-consulting-slate-blue-100 text-consulting-slate-blue-700'
                      : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                  }`}
                >
                  {config.label}
                </button>
              );
            })}
          </div>
        </div>
        
        <div className="space-y-3">
          {filteredControls.map(control => (
            <ControlCard key={control.id} control={control} />
          ))}
        </div>
      </div>
      
      {/* Help Link */}
      <div className="mt-8 text-center">
        <a
          href="/elevation/documentation/security"
          className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 font-medium"
        >
          View full security documentation
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}


